import { baseUrl, httpClient } from 'src/pages/admin/providers/AppDataProvider';

import type {
  ApiResponse,
  BulkUpdateFocusMatchSchedulesPayload,
  FocusMatchRule,
  FocusMatchSchedule,
  MatchesByStage,
  RawMatchesByStage,
  UpdateFocusMatchRulePayload,
} from './types';
import {
  normalizeLiveOps,
  normalizeMatchesByStage,
  normalizeRule,
  normalizeSchedule,
  normalizeSchedules,
} from './utils';

const resourceBase = 'world-cup/focus-match-config';
const matchDataBase = 'world-cup/match-data';

function unwrap<T>(json: ApiResponse<T> | T): T {
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as ApiResponse<T>).data;
  }

  return json as T;
}

async function request<T>(resource: string, path: string, options: RequestInit = {}): Promise<T> {
  const { json } = await httpClient(`${baseUrl}/${resource}${path}`, options as any);

  return unwrap<T>(json);
}

export const focusMatchConfigApi = {
  async getLiveOps(signal?: AbortSignal) {
    const data = await request<unknown>(resourceBase, '/live-ops', {
      method: 'GET',
    } as any);

    return normalizeLiveOps(data);
  },

  async getSchedules(signal?: AbortSignal) {
    const data = await request<unknown>(resourceBase, '/schedules', {
      method: 'GET',
      signal,
    } as any);

    return normalizeSchedules(data);
  },

  async bulkUpdateSchedules(payload: BulkUpdateFocusMatchSchedulesPayload) {
    return request<unknown>(resourceBase, '/schedules', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async getRule(signal?: AbortSignal): Promise<FocusMatchRule | null> {
    const data = await request<unknown>(resourceBase, '/rule', {
      method: 'GET',
      signal,
    } as any);

    return normalizeRule(data);
  },

  updateRule(payload: UpdateFocusMatchRulePayload) {
    return request<FocusMatchRule>(resourceBase, '/rule', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async getAvailableMatches(signal?: AbortSignal): Promise<MatchesByStage> {
    const data = await request<RawMatchesByStage>(matchDataBase, '/matches', {
      method: 'GET',
      signal,
    } as any);

    return normalizeMatchesByStage(data);
  },
};
